import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { TYPOGRAPHY, RESPONSIVE } from '@/design-system';

export interface PlatformStatItemProps {
  icon: ReactNode;
  value: number | undefined;
  label: ReactNode;
  isLoading?: boolean;
  formatNumber?: (n: number) => string;
  variant?: 'default' | 'compact' | 'card';
  className?: string;
}

const defaultFormatNumber = (n: number) => n.toLocaleString();

export function PlatformStatItem({
  icon,
  value,
  label,
  isLoading = false,
  formatNumber = defaultFormatNumber,
  variant = 'default',
  className,
}: PlatformStatItemProps) {
  if (variant === 'card') {
    return (
      <Card className={cn('hover:shadow-md transition-shadow', className)}>
        <CardContent className="p-6">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                {icon}
              </div>
              <div className={cn(TYPOGRAPHY.metricValueLarge, 'text-foreground mb-1')}>
                {formatNumber(value || 0)}
              </div>
              <div className={cn(TYPOGRAPHY.cardDescription)}>
                {label}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn(
        'flex items-center gap-2.5 sm:gap-3 py-2.5 sm:py-3 first:pt-0 last:pb-0',
        'hover:bg-slate-50/80 transition-colors rounded-md -mx-1 px-1',
        className
      )}>
        {isLoading ? (
          <>
            <Skeleton className="w-6 h-6 sm:w-7 sm:h-7 rounded-md shrink-0" />
            <div className="flex-1">
              <Skeleton className="h-4 sm:h-5 w-12 mb-0.5" />
              <Skeleton className="h-3 sm:h-4 w-20" />
            </div>
          </>
        ) : (
          <>
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm sm:text-base font-semibold text-foreground">
                {formatNumber(value || 0)}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground truncate">
                {label}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Default variant - horizontal with icon badge
  return (
    <div className={cn(RESPONSIVE.flexRowCenterGap2, className)}>
      {isLoading ? (
        <>
          <Skeleton className="w-8 h-8 rounded-lg shrink-0" />
          <div>
            <Skeleton className="h-5 w-12 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </>
      ) : (
        <>
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            {icon}
          </div>
          <div>
            <div className={cn(TYPOGRAPHY.metricValueMedium, 'text-foreground')}>
              {formatNumber(value || 0)}
            </div>
            <div className={cn(TYPOGRAPHY.cardDescription)}>
              {label}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
